import {
  Component,
  Input,
  ViewEncapsulation,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  DoCheck
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType, Chart, registerables } from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

@Component({
  standalone: true,
  selector: 'app-pie-chart',
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PieChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, DoCheck {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  @Input() view: [number, number] = [400, 400];
  @Input() scheme: any = null; // Color scheme for compatibility
  @Input() results: any[] = [];
  @Input() animations: boolean = true;
  @Input() tooltipDisabled: boolean = false;
  @Input() legend: boolean = true;
  @Input() legendTitle: string = 'Legend';
  @Input() legendPosition: string = 'right';
  @Input() showLabels: boolean = true;
  @Input() explodeSlices: boolean = false;
  @Input() doughnut: boolean = false;
  @Input() arcWidth: number = 0; // For doughnut thickness
  @Input() gradient: boolean = false;

  @Output() select: EventEmitter<any> = new EventEmitter();
  @Output() activate: EventEmitter<any> = new EventEmitter();
  @Output() deactivate: EventEmitter<any> = new EventEmitter();

  // Chart.js Pie Chart Configuration
  public pieChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: []
  };

  public pieChartType: ChartType = 'pie';
  
  // Default color palette
  private defaultColors: string[] = [
    'rgba(1, 87, 155, 0.8)',   // Blue
    'rgba(156, 39, 176, 0.8)',  // Purple
    'rgba(0, 150, 136, 0.8)',  // Teal
    'rgba(255, 152, 0, 0.8)',   // Orange
    'rgba(76, 175, 80, 0.8)',   // Green
    'rgba(244, 67, 54, 0.8)',   // Red
    'rgba(63, 81, 181, 0.8)',   // Indigo
    'rgba(255, 193, 7, 0.8)',   // Amber
    'rgba(233, 30, 99, 0.8)',   // Pink
    'rgba(121, 85, 72, 0.8)'    // Brown
  ];

  // Get chart options
  get pieChartOptions(): ChartConfiguration['options'] {
    const baseOptions: ChartConfiguration['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      animation: this.animations ? {} : false,
      interaction: {
        intersect: false
      },
      plugins: {
        legend: {
          display: false // We'll use custom legend
        },
        tooltip: {
          enabled: !this.tooltipDisabled,
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const dataArray = Array.isArray(context.dataset.data) ? context.dataset.data : [];
              const total = dataArray.reduce((a: any, b: any) => {
                const aVal = typeof a === 'number' ? a : 0;
                const bVal = typeof b === 'number' ? b : 0;
                return aVal + bVal;
              }, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      },
      elements: {
        arc: {
          borderWidth: 2,
          borderColor: '#fff'
        }
      },
      onHover: (event: any, active: any) => {
        if (active && active.length > 0) {
          this.onActivate(active);
        } else {
          this.onDeactivate(event);
        }
      }
    };

    // Set chart type based on doughnut input
    if (this.doughnut) {
      this.pieChartType = 'doughnut';
      // Doughnut cutout is handled via dataset options, not plugins
      if (baseOptions && baseOptions.elements && baseOptions.elements.arc) {
        (baseOptions.elements.arc as any).borderWidth = 2;
      }
    } else {
      this.pieChartType = 'pie';
    }

    return baseOptions || {};
  }

  // Chart.js specific methods
  private lastDataSignature: string = '';

  ngDoCheck(): void {
    const currentSig = this.buildDataSignature();
    if (currentSig !== this.lastDataSignature) {
      this.lastDataSignature = currentSig;
      this.updateChartData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] || changes['doughnut'] || changes['arcWidth'] || changes['scheme']) {
      this.lastDataSignature = this.buildDataSignature();
      this.updateChartData();
    }
  }

  ngOnInit(): void {
    this.updateChartData();
  }

  ngAfterViewInit(): void {
    this.updateChartData();
    window.addEventListener('resize', this.onResize.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
  }

  private onResize(): void {
    setTimeout(() => {
      if (this.chart && this.chart.chart) {
        this.chart.chart.resize();
        this.chart.update();
      }
    }, 100);
  }

  private updateChartData(): void {
    if (!this.results || this.results.length === 0) {
      this.pieChartData = { labels: [], datasets: [] };
      if (this.chart && this.chart.chart) {
        this.chart.update('none');
      }
      return;
    }

    // Extract labels and values from results
    const labels = this.results.map(item => item.name || '');
    const values = this.results.map(item => item.value || 0);

    // Generate colors
    const colors = this.generateColors(this.results.length);

    // Prepare dataset
    const dataset: any = {
      label: 'Data',
      data: values,
      backgroundColor: colors.map(c => c.background),
      borderColor: colors.map(c => c.border),
      borderWidth: 2
    };
    
    // Add cutout for doughnut chart
    if (this.doughnut) {
      dataset.cutout = this.arcWidth > 0 ? `${this.arcWidth}%` : '50%';
    }

    this.pieChartData = {
      labels: labels,
      datasets: [dataset]
    };

    // Update chart if it exists
    if (this.chart && this.chart.chart) {
      this.chart.update(this.animations ? 'default' : 'none');
    }
  }

  private generateColors(count: number): Array<{ background: string; border: string }> {
    const colors: Array<{ background: string; border: string }> = [];
    
    // Use scheme if provided, otherwise use default colors
    let colorArray: string[] = this.defaultColors;
    
    if (this.scheme && Array.isArray(this.scheme.domain)) {
      colorArray = this.scheme.domain;
    }

    for (let i = 0; i < count; i++) {
      const color = colorArray[i % colorArray.length];
      colors.push({
        background: color,
        border: '#ffffff'
      });
    }

    return colors;
  }

  private buildDataSignature(): string {
    try {
      const r = Array.isArray(this.results)
        ? this.results.map((it: any) => [it?.name, it?.value])
        : [];
      return JSON.stringify({ r, doughnut: this.doughnut, arcWidth: this.arcWidth });
    } catch {
      return Math.random().toString();
    }
  }

  // Event handlers for Chart.js
  onChartSelect(event: any): void {
    this.select.emit(event);
  }

  onChartActivate(event: any): void {
    this.activate.emit(event);
  }

  onChartDeactivate(event: any): void {
    this.deactivate.emit(event);
  }

  // Legacy methods for compatibility
  onSelect(event: any): void {
    this.select.emit(event);
  }

  onActivate(event: any): void {
    this.activate.emit(event);
  }

  onDeactivate(event: any): void {
    this.deactivate.emit(event);
  }

  // Helper methods for template
  getLegendColor(index: number): string {
    const dataset = this.pieChartData.datasets[0];
    if (!dataset || !dataset.backgroundColor) return '#ccc';
    const colors = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor : [];
    return colors[index] || '#ccc';
  }

  getLegendValue(index: number): number | null {
    const dataset = this.pieChartData.datasets[0];
    if (!dataset || !dataset.data) return null;
    const data = Array.isArray(dataset.data) ? dataset.data : [];
    const value = data[index];
    // Handle different data types - return number or null
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return value;
    if (Array.isArray(value)) return value[0] || null;
    return null;
  }
}

